import { Fragment, useContext, useState, useRef, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { EntityContext } from '../../context/EntityContext';
import { UserContext } from '../../context/UserContext';
import {TfiClose} from "react-icons/tfi";
import { APITransferPlanToInspire, APICreateInspireOrganization, APIAutoFillInspireBucket, APICreateNewBucketForPlan } from '../../api/integrations/Integrations';
import { APIGetPlanDetails, APIGetDownloadPlanUrl } from '../../api/plans/Plans';
import { LoadingIndicator } from '../loadingIndicator/loadingIndicator';


export const InspireModal = () => {
    const cancelButtonRef = useRef(null);
    const { openInspireModal, setOpenInspireModal, currentEntity, currentSetting} = useContext(EntityContext);
    const [loading, setLoading] = useState(false);
    const { company } = useContext(UserContext);
    const [ planDetails, setPlanDetails ] = useState(null);
    const [ uploadedAt, setUploadedAt ] = useState(null);

    const onFinish = async () => {
        setLoading(true);
        try {
            const inspireIntegration = company.integrations.find(integration => integration.type === 'inspire');
            if (!inspireIntegration || !inspireIntegration.details || !inspireIntegration.details.orgId) {
                await APICreateInspireOrganization(company.id, {
                    name: company.name || company.id,
                    contactEmail: company.email || '',
                });
            }
            // check if planDetails.informations.inspire.bucketId is set
            let bucketId = planDetails?.informations?.inspire?.bucketId;
            if (!bucketId) {
                const newBucketId = await APICreateNewBucketForPlan(company.id, currentEntity.id);
                // Update the planDetails with the new bucketId
                setPlanDetails(prevDetails => ({
                    ...prevDetails,
                    informations: {
                        ...(prevDetails.informations || {}),
                        inspire: {
                            ...((prevDetails.informations && prevDetails.informations.inspire) || {}),
                            bucketId: newBucketId
                        }
                    }
                }));
                bucketId = newBucketId;
            }
            // Transfer the plan to Inspire
            const downloadUrl = await APIGetDownloadPlanUrl(currentEntity.id);
            if (!downloadUrl) {
                console.error("No download URL found for plan.");
                setLoading(false);
                return false;
            }
            await APITransferPlanToInspire(company.id, bucketId, currentEntity.id, downloadUrl);
            await APIAutoFillInspireBucket(company.id, currentEntity.id);

        } catch (error) {
            console.error("Error transferring plan to Inspire:", error);
            setLoading(false);
            return false;
        }
        setOpenInspireModal(false);
        setLoading(false);
        return true;
    }

    const onInspireCancel = async () => {
        setLoading(true);
        setOpenInspireModal(false);
        setLoading(false);
    }

    useEffect(() => {
        const fetchPlanDetails = async () => {
            if (currentEntity?.id && company?.id) {
                try {
                    const planDetails = await APIGetPlanDetails(currentEntity.id, company.id);
                    const uploadedAt = planDetails?.informations?.inspire?.uploadedAt;
                    if (uploadedAt) {
                        setUploadedAt(new Date(uploadedAt));
                    } else {
                        setUploadedAt(null);
                    }
                    setPlanDetails(planDetails);
                } catch (error) {
                    console.error("Error fetching plan details:", error);
                }
            }
        };
        fetchPlanDetails();
    }, [currentEntity?.id, company.id]);

    return (
        <Transition.Root show={openInspireModal} as={Fragment}>
            <Dialog
            className="relative z-10"
            initialFocus={cancelButtonRef}
            onClose={onInspireCancel}
            >
            <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>
    
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex h-full items-center justify-center sm:px-4 sm:pb-4 text-center pt-[65px] sm:px-4 lg:p-10 max-w-5xl mx-auto">
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <Dialog.Panel className='flex flex-col max-h-full sm:max-h-max h-full rounded-lg bg-white text-left shadow-xl transition-all w-full relative'>
                    <button
                        type='button'
                        className='absolute z-40 lg: lg:-top-6 lg:-right-6 top-2 right-2'
                        onClick={onInspireCancel}
                        disabled={loading}
                        ref={cancelButtonRef}
                    >
                        <TfiClose className={"lg:size-16 border-gray-500/80 border-2 bg-white hover:bg-gray-300 lg:p-4 size-12 p-2 lg:rounded-full rounded-md"}/>
                    </button>
                    <div className='block md:flex w-full md:overflow-hidden'>
                        <div className={'flex bg-white flex-col w-full'}>
                            <div>
                                <div className='flex items-center mb-8  m-8 gap-2'>
                                    <Dialog.Title
                                        as='h3'
                                        className='font-bold text-2xl leading-6 text-gray-900'>
                                        We transform - INSPIRE Veröffentlichung
                                    </Dialog.Title>
                                </div>
                            </div>
                            {loading ? (
                                <div className='flex flex-col items-center justify-center h-full'>
                                    <LoadingIndicator />
                                    <p className='text-gray-600 text-sm mt-8'>Daten werden übertragen...</p>
                                    <p className='text-gray-600 text-sm'>Bitte warten Sie einen Moment.</p>
                                </div>
                            ) : (
                                <div className={'flex flex-col flex-1 overflow-hidden justify-center align-center'}>
                                    {uploadedAt ? (
                                        <div className='flex items-center justify-between px-8'>
                                            <span className='text-sm pb-8 text-gray-600'>Der Plan <b>{currentEntity.name}</b> wurde am : {uploadedAt.toLocaleDateString()} {uploadedAt.toLocaleTimeString()} zu Inspire übertragen.</span>
                                        </div>
                                    ): (
                                        <div className='flex flex-col overflow-y-auto'>
                                            <div className='flex flex-col gap-4 p-8'>
                                                <p className='text-gray-600 text-sm leading-relaxed mb-2'>
                                                    Sie können den Plan <b>{currentEntity.name}</b> zu Inspire übertragen.<br/> Bitte beachte, dass dies nicht rückgängig gemacht werden kann. Der Plan wird anschließend unter<br/> <a className={"font-bold"} href={"https://inspire-geoportal.ec.europa.eu/"}>https://inspire-geoportal.ec.europa.eu/</a> aufrufbar sein.
                                                </p>
                                                <p className='text-gray-600 text-sm leading-relaxed mb-2'>
                                                    Stelle sicher, dass du alle notwendigen Informationen überprüft hast, bevor du fortfährst.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="p-8 pt-0 sm:flex-row-reverse flex w-full flex-col-reverse gap-4">
                                { uploadedAt ? (
                                    <button
                                        type="button"
                                        disabled={loading}
                                        className={`${loading ? 'cursor-progress' : ''} text-center truncate items-center rounded-md bg-white border-gray-900 border-2 disabled:text-gray-600/40 disabled:border-gray-500 px-4 py-3 text-sm font-semibold text-gray-900 `}
                                        onClick={onFinish}
                                    >
                                        <p>{loading ? 'Übertrage Daten...' : 'Plan aktualisieren'}</p>
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        disabled={loading}
                                        className={`${loading ? 'cursor-progress' : ''} text-center truncate items-center rounded-md bg-white border-gray-900 border-2 disabled:text-gray-600/40 disabled:border-gray-500 px-4 py-3 text-sm font-semibold text-gray-900 `}
                                        onClick={onFinish}
                                    >
                                        <p>{loading ? 'Übertrage Daten...' : 'Plan veröffentlichen'}</p>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    </Dialog.Panel>
    
                </Transition.Child>
                </div>
            </div>
            </Dialog>
        </Transition.Root>
    );
}
